// Mirrored in applications/platform-app/components/web-analytics — change both.
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { WebDim, WebFilter, WebRange, RangePreset } from './types';
import { FILTER_DIMS, METRIC_IDS, sameFilter, type MetricId } from './format';

/**
 * Dashboard state lives in the URL query string so every view is a shareable
 * link: ?site=&tab=&range=&from=&to=&compare=0&metric=&f=dim:value (repeatable).
 * `download` is read once by the dashboard (export email links) and removed.
 */
export const TAB_IDS = ['overview', 'realtime', 'acquisition', 'pages', 'conversions', 'audience', 'security', 'settings'] as const;
export type TabId = typeof TAB_IDS[number];
export const PRESETS: RangePreset[] = ['today', '7d', '30d', '90d', '12m'];
const DATE = /^\d{4}-\d{2}-\d{2}$/;
const MAX_FILTERS = 10;

export interface DashboardState {
  site: string;
  tab: TabId;
  range: WebRange;
  filters: WebFilter[];
  metric: MetricId;
}

export function parseFilters(values: string[]): WebFilter[] {
  const out: WebFilter[] = [];
  for (const v of values) {
    const i = v.indexOf(':');
    if (i <= 0) continue;
    const f = { dim: v.slice(0, i) as WebDim, value: v.slice(i + 1) };
    // Only dims the lake can filter on — a hand-edited ?f=exit:… would 400 every query.
    if (!FILTER_DIMS.includes(f.dim)) continue;
    if (f.value && !out.some((x) => sameFilter(x, f))) out.push(f);
  }
  return out.slice(0, MAX_FILTERS);
}

export function readState(p: URLSearchParams, defaultSite: string, fixedSiteId?: string): DashboardState {
  const tab = (TAB_IDS as readonly string[]).includes(p.get('tab') || '') ? (p.get('tab') as TabId) : 'overview';
  const r = p.get('range') || '30d';
  const from = p.get('from') || '';
  const to = p.get('to') || '';
  const compare = !['0', 'false'].includes(p.get('compare') || '');
  const range: WebRange = r === 'custom' && DATE.test(from) && DATE.test(to) && from <= to
    ? { range: 'custom', from, to, compare }
    : { range: (PRESETS as string[]).includes(r) ? (r as RangePreset) : '30d', compare };
  const m = p.get('metric') || 'visitors';
  return {
    site: fixedSiteId || p.get('site') || defaultSite,
    tab,
    range,
    filters: parseFilters(p.getAll('f')),
    metric: (METRIC_IDS as string[]).includes(m) ? (m as MetricId) : 'visitors',
  };
}

export function writeState(prev: URLSearchParams, s: DashboardState, fixedSiteId?: string): URLSearchParams {
  const p = new URLSearchParams(prev);
  for (const k of ['site', 'tab', 'range', 'from', 'to', 'compare', 'metric', 'f']) p.delete(k);
  if (!fixedSiteId) p.set('site', s.site);
  if (s.tab !== 'overview') p.set('tab', s.tab);
  if (s.range.range !== '30d') p.set('range', s.range.range);
  if (s.range.range === 'custom') { p.set('from', s.range.from || ''); p.set('to', s.range.to || ''); }
  if (!s.range.compare) p.set('compare', '0');
  if (s.metric !== 'visitors') p.set('metric', s.metric);
  for (const f of s.filters) p.append('f', `${f.dim}:${f.value}`);
  return p;
}

export function useDashboardState(opts: { defaultSite: string; fixedSiteId?: string }) {
  const [params, setParams] = useSearchParams();
  const state = useMemo(() => readState(params, opts.defaultSite, opts.fixedSiteId), [params, opts.defaultSite, opts.fixedSiteId]);

  const update = useCallback((patch: Partial<DashboardState> | ((s: DashboardState) => Partial<DashboardState>)) => {
    setParams((prev) => {
      const cur = readState(prev, opts.defaultSite, opts.fixedSiteId);
      const next = { ...cur, ...(typeof patch === 'function' ? patch(cur) : patch) };
      return writeState(prev, next, opts.fixedSiteId);
    }, { replace: true });
  }, [setParams, opts.defaultSite, opts.fixedSiteId]);

  const actions = useMemo(() => ({
    setSite: (site: string) => update({ site, filters: [] }),
    setTab: (tab: TabId) => update({ tab }),
    setRange: (range: WebRange) => update({ range }),
    setCompare: (compare: boolean) => update((s) => ({ range: { ...s.range, compare } })),
    setMetric: (metric: MetricId) => update({ metric }),
    addFilter: (f: WebFilter) => update((s) => ({
      filters: s.filters.some((x) => sameFilter(x, f)) ? s.filters : [...s.filters, f].slice(0, MAX_FILTERS),
      // Realtime and Settings are not filterable views — a new chip lands on Overview (as in the mockup).
      tab: s.tab === 'realtime' || s.tab === 'settings' ? 'overview' : s.tab,
    })),
    removeFilter: (f: WebFilter) => update((s) => ({ filters: s.filters.filter((x) => !sameFilter(x, f)) })),
    clearFilters: () => update({ filters: [] }),
  }), [update]);

  const takeParam = useCallback((key: string): string | null => params.get(key), [params]);
  const dropParam = useCallback((key: string) => {
    setParams((prev) => { const p = new URLSearchParams(prev); p.delete(key); return p; }, { replace: true });
  }, [setParams]);

  return { state, ...actions, takeParam, dropParam };
}
